/**
 * Shared car/parts catalog.
 *
 * Primary: JSON hosted on Plutoo Hub Storage (jupiter-to-plutoo), cached in localStorage.
 * Fallback: bundled auto-parts-db chunk if the hub is unreachable.
 *
 * Do not import 'auto-parts-db' from app components — use this file.
 */

const CACHE_KEY = 'plutoo-parts-catalog-v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const HUB_BUCKET = 'jupiter-to-plutoo.firebasestorage.app';
const CATALOG_PREFIX = 'parts-catalog';
/** Stable download token — publish script writes the same token so URLs never change. */
const DOWNLOAD_TOKEN = '7c4e1a2b-9d83-4f06-b1e5-2a8c9d0f4e71';

const DEFAULT_BASE = `https://firebasestorage.googleapis.com/v0/b/${HUB_BUCKET}/o/${encodeURIComponent(CATALOG_PREFIX)}`;

let brandsData = [];
let partsData = [];
let loaded = false;
let source = 'none';
let loadPromise = null;

const catalogFileUrl = (fileName) => {
  const override = (process.env.REACT_APP_PARTS_CATALOG_BASE_URL || '').replace(/\/$/, '');
  if (override) {
    return `${override}/${fileName}`;
  }
  return `${DEFAULT_BASE}%2F${encodeURIComponent(fileName)}?alt=media&token=${DOWNLOAD_TOKEN}`;
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.brands) || !Array.isArray(parsed.parts)) return null;
    if (!parsed.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (brands, parts, version) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        version: version || '1',
        fetchedAt: Date.now(),
        brands,
        parts,
      })
    );
  } catch (e) {
    console.warn('Could not cache parts catalog', e);
  }
};

const applyCatalog = (brands, parts, from) => {
  brandsData = Array.isArray(brands) ? brands : [];
  partsData = Array.isArray(parts) ? parts : [];
  loaded = true;
  source = from;
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Catalog fetch failed (${response.status})`);
  }
  return response.json();
};

const loadFromHub = async () => {
  const [brands, parts, manifest] = await Promise.all([
    fetchJson(catalogFileUrl('brands.json')),
    fetchJson(catalogFileUrl('parts.json')),
    fetchJson(catalogFileUrl('manifest.json')).catch(() => null),
  ]);
  if (!Array.isArray(brands) || !Array.isArray(parts)) {
    throw new Error('Invalid catalog payload');
  }
  applyCatalog(brands, parts, 'hub');
  writeCache(brands, parts, manifest?.version);
};

const loadFromBundle = async () => {
  const local = await import('./autoPartsDbLocal');
  const data = local.default || local;
  applyCatalog(data.brands, data.parts, 'bundle');
};

export const ensurePartsCatalog = async () => {
  if (loaded) return { source, brandCount: brandsData.length, partCount: partsData.length };
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const cached = readCache();
    if (cached) {
      applyCatalog(cached.brands, cached.parts, 'cache');
      loadFromHub().catch((hubErr) => {
        console.warn('Hub catalog refresh failed, using cache', hubErr);
      });
      return { source, brandCount: brandsData.length, partCount: partsData.length };
    }

    try {
      await loadFromHub();
    } catch (hubErr) {
      console.warn('Hub catalog unavailable, using bundled fallback', hubErr);
      await loadFromBundle();
    }

    return { source, brandCount: brandsData.length, partCount: partsData.length };
  })();

  return loadPromise;
};

export const getCatalogSource = () => source;

const norm = (s) => (s || '').toString().trim().toLowerCase();

export const getBrands = () => brandsData.map((b) => b.brand);

export const getBrand = (brandName) =>
  brandsData.find((b) => norm(b.brand) === norm(brandName)) || null;

export const getModelsByBrand = (brandName) => {
  const brand = getBrand(brandName);
  return brand ? brand.models : [];
};

export const getGenerations = (brandName, modelName) => {
  const models = getModelsByBrand(brandName);
  const model = models.find((m) => norm(m.name) === norm(modelName));
  return model ? model.generations : [];
};

export const getGenerationByYear = (brandName, modelName, year) => {
  const generations = getGenerations(brandName, modelName) || [];
  return (
    generations.find(
      (g) => g.yearFrom <= year && (g.yearTo === null || g.yearTo >= year)
    ) || null
  );
};

export const searchModels = (query) => {
  const q = norm(query);
  const results = [];
  for (const b of brandsData) {
    for (const m of b.models || []) {
      if (norm(m.name).includes(q)) {
        results.push({ brand: b.brand, model: m });
      }
    }
  }
  return results;
};

export const getParts = () => partsData;

export const getPartBySlug = (slug) =>
  partsData.find((p) => p.slug === slug) || null;

export const searchParts = (query) => {
  const q = norm(query);
  return partsData.filter((p) => norm(p.name).includes(q) || (p.slug || '').includes(q));
};

export const getPartSlugs = () => partsData.map((p) => p.slug);
